import { ChannelType } from '@aaa-ncnu-ie/mypolicy-api-registrations';
import { Stack, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useOrganicRegistrationForm } from '..';
import { useSendCodeMutation } from '../../../../../api/registration/otvc/sendCode';
import Verification from '../../../../../assets/icons/Verification';
import Button from '../../../../../components/styledComponents/Button';
import { LinkWithNoRouter } from '../../../../../components/styledComponents/Link';
import colors from '../../../../../constants/colors';
import { useDirectAccessAnalytics } from '../../../../../hooks/useAnalytics';
import { useSnackbarsContext } from '../../../../../store/SnackbarProvider';
import SplitView from '../../../SplitView';

const VerificationMethod = () => {
  const navigate = useNavigate();
  const { formValues, otvcValues, setOtvcValues } =
    useOrganicRegistrationForm();
  const { closeSnackbar, enqueueSnackbar } = useSnackbarsContext();
  const sendCodeMutation = useSendCodeMutation();
  const { track } = useDirectAccessAnalytics();

  const showGenericErrorBanner = () => {
    track('User Messaged', {
      event_type: 'Messaged',
      location: 'Feature Card',
      message_type: 'Error',
      contents: 'We are unable to process your request right now.',
    });
    enqueueSnackbar({
      key: 'policy-search-organic-registration-error',
      variant: 'error',
      title: 'We are unable to process your request right now.',
      content: (
        <>
          Please check the details and try again, or contact Service at {}
          <LinkWithNoRouter
            href="tel:+888.980.5650"
            underline="none"
            sx={{
              fontSize: {
                mobile: 16,
                tablet: 18,
              },
              fontWeight: 400,
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              color: colors.csaaSpectrumBlue50,
            }}
          >
            888.980.5650
          </LinkWithNoRouter>
          .
        </>
      ),
    });
  };

  const requestCode = async () => {
    const { email } = formValues;
    closeSnackbar();
    try {
      await sendCodeMutation.mutateAsync({
        channelType: ChannelType.EMAIL,
        channelValue: email,
      });

      if (!otvcValues?.codeSent) {
        setOtvcValues((current) => ({
          ...current,
          codeSent: true,
          flow: 'Email',
        }));
      }
      navigate('email');
    } catch (err) {
      showGenericErrorBanner();
    }
  };

  useEffect(() => {
    closeSnackbar();
  }, []);

  return (
    <SplitView data-testid="organic-registration-1" Icon={<Verification />}>
      <Stack
        justifyContent={{ mobile: 'flex-start', desktop: 'center' }}
        direction="column"
        sx={{ px: { mobile: 2, tablet: 14, desktop: 11 }, height: '100%' }}
      >
        <Typography
          variant="h1"
          sx={{
            fontWeight: 700,
            fontSize: { mobile: 33, tablet: 44 },
            lineHeight: { mobile: '48px', desktop: '56px' },
          }}
        >
          Verification code
        </Typography>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 500,
            fontSize: { mobile: 23, tablet: 28 },
            lineHeight: { mobile: '32px', desktop: '40px' },
            mt: 1,
            color: colors.csaaDark,
          }}
        >
          We&apos;ll send you a one-time code to help you sign up.
        </Typography>
        <Typography
          variant="h2"
          sx={{
            fontFamily: 'Cabin',
            fontWeight: 400,
            fontSize: { mobile: 14, tablet: 16 },
            lineHeight: '24px',
            mt: 3,
            color: colors.csaaDark,
          }}
        >
          How would you like to receive your code?
        </Typography>
        <Stack
          direction={{ mobile: 'column', tablet: 'row' }}
          justifyContent="space-between"
          sx={{ mt: { mobile: 3, tablet: 5 } }}
          spacing={2}
        >
          <Button
            onClick={() => {
              requestCode();
            }}
            data-testid="email-otvc-link"
            role="link"
            fullWidth
            loading={sendCodeMutation.isPending}
          >
            Email
          </Button>
          <Button
            onClick={() => {
              closeSnackbar();
              navigate('sms');
            }}
            role="link"
            data-testid="sms-otvc-link"
            fullWidth
            disabled={sendCodeMutation.isPending}
          >
            Text
          </Button>
        </Stack>
      </Stack>
    </SplitView>
  );
};

export default VerificationMethod;
